import { Component, computed, inject, input, OnInit, resource } from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Breadcrumb, BreadcrumbNav, Button } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { AnalysisConsumptionFilters } from '../../components/analysis-consumption-filters/analysis-consumption-filters';
import { AnalysisConsumptionGraphCard } from '../../components/analysis-consumption-graph-card/analysis-consumption-graph-card';
import { ConsumptionFilter, ConsumptionStore } from '../../stores/consumption.store';
import { format } from 'date-fns';
import { TranslocoPipe } from '@jsverse/transloco';
import { getFilterFromQueryParams } from './definitions';
import { AnalysisConsumptionDetailsKpiCard } from '../../components/analysis-consumption-details-kpi-card/analysis-consumption-details-kpi-card';
import { AnalysisConsumptionDetailsPeakloadKpiCard } from '../../components/analysis-consumption-details-peakload-kpi-card/analysis-consumption-details-peakload-kpi-card';
import { Api } from '../../../../core/clients/omni-core/api';
import {
  listEnergyInterfaces,
  ListEnergyInterfaces$Params,
  listLocations,
  ListLocations$Params,
} from '../../../../core/clients/omni-core/functions';
import {
  EnergyInterfaceListResponse,
  LocationListResponse,
} from '../../../../core/clients/omni-core/models';

@Component({
  selector: 'app-analysis-consumption-page',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    PageTitle,
    RouterLink,
    BreadcrumbNav,
    Breadcrumb,
    Button,
    NgIcon,
    AnalysisConsumptionFilters,
    AnalysisConsumptionGraphCard,
    TranslocoPipe,
    AnalysisConsumptionDetailsKpiCard,
    AnalysisConsumptionDetailsPeakloadKpiCard,
  ],
  templateUrl: './analysis-consumption-page.html',
})
export class AnalysisConsumptionPage implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private consumptionStore = inject(ConsumptionStore);
  private api = inject(Api);

  organizationId = input.required<string>();

  protected filter = this.consumptionStore.filter;
  protected data = computed(() => this.consumptionStore.get(this.filter()));
  private utilityType = computed(() => this.filter().data.utilityType);
  private flowDirection = computed(() => {
    const direction = this.filter().data.direction;
    return direction === 'PRODUCTION' ? 'FEED_IN' : direction;
  });
  private locationID = computed(() => this.filter().data.locationID);

  protected locations = resource<LocationListResponse, ListLocations$Params>({
    params: () => ({
      id: this.organizationId(),
      utilityType: this.utilityType(),
      flowDirection: this.flowDirection(),
      limit: 999,
    }),
    loader: ({ params }) => this.api.invoke(listLocations, params),
  });

  protected energyInterfaces = resource<EnergyInterfaceListResponse, ListEnergyInterfaces$Params>({
    params: () => ({
      id: this.organizationId(),
      utilityType: this.utilityType(),
      flowDirection: this.flowDirection(),
      limit: 999,
      locationID: this.locationID() || undefined,
    }),
    loader: ({ params }) => this.api.invoke(listEnergyInterfaces, params),
  });

  ngOnInit(): void {
    this.initFilter();
  }

  private initFilter() {
    this.consumptionStore.filter.set(getFilterFromQueryParams(this.activatedRoute.snapshot));
    this.consumptionStore.onChangeFilter();
  }

  protected async onChangeDate(date: Date) {
    this.consumptionStore.filter.update((f) => ({ ...f, date }));
    this.replaceQueryParams(this.consumptionStore.filter());
    this.consumptionStore.onChangeFilter();
  }

  protected async onChangeGranularity(granularity: ConsumptionFilter['granularity']) {
    this.consumptionStore.filter.update((f) => ({ ...f, granularity }));
    this.replaceQueryParams(this.consumptionStore.filter());
    this.consumptionStore.onChangeFilter();
  }

  protected onChangeConsumptionFilter(filter: ConsumptionFilter) {
    this.consumptionStore.filter.set(filter);
    this.replaceQueryParams(filter);
    this.consumptionStore.onChangeFilter();
  }

  private replaceQueryParams(filter: ConsumptionFilter) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        view: filter.view || null,
        granularity: filter.granularity || null,
        date: format(filter.date, 'yyyy-MM-dd'),
        utilityType: filter.data.utilityType,
        direction: filter.data.direction,
        locationID: filter.data.locationID || null,
        energyInterfaceID: filter.data.energyInterfaceID || null,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onClickReload() {
    this.consumptionStore.load(this.consumptionStore.filter());
  }
}
